import React, { FC, useContext } from 'react'
import { Trans, t } from '@lingui/macro'
import classNames from 'classnames'

import { formatDateAndTimeShort } from 'front-end/src/utils/dateHelpers'

import UserAvatar from '../../../components/common/user/user-avatar/UserAvatar'
import BorderedBox from '../../../components/common/bordered-box/BorderedBox'
import Tag from '../../../components/common/tag/Tag'
import { AnalyticsHeader } from '../AnalyticsHeader'
import { QuizResults } from '../../topic-detail-page/components/quizes/quizResults/QuizResults'
import Loader from '../../../components/common/loader/Loader'
import { LinkableUserAvatar } from '../../../components/common/user/linkable-user-avatar/LinkableUserAvatar'
import {
  EnrolmentDetailAnalyticsFragment,
  useTopicEnrolmentQuery
} from './WorkbooksDetail.codegen'
import EnrolmentProgressDetail from './components/enrolment-progress-detail/EnrolmentProgressDetail'
import { SmallMarbleRating } from '../../../components/common/marble-rating/SmallMarbleRating'
import { AnalyticsContext } from '../AnalyticsContext'
import { useParams } from 'react-router'
import { useHistory } from 'react-router-dom'
import { videoAssessmentTopicVersionTypesForFE } from '../../topic-detail-page/StudentTopicDetail'
import { TopicVersionType } from '../../../graphql/private/PrivateSchemaTypes'
import {
  StatusArchivedGrey,
  StatusProgressFailed,
  StatusProgressPassed
} from 'front-end/src/components/common/status/Status'
import { Link } from '../../../components/common/link/Link'
import { linkBuilder } from 'shared/linkBuilder'
import { Box, ButtonIcon } from '../../../components'
import { brandColors } from 'shared/basicPalette'
import { IconNewTab } from '../../../components/common/icon/IconNewTab'
import { getScoreString } from 'front-end/src/pages/analytics/utils'

const WorkbooksDetails: FC<{}> = () => {
  const history = useHistory()
  const { userTopicId } = useParams<{ userTopicId: string }>()
  const { filterAsQueryVariables } = useContext(AnalyticsContext)
  const { data, loading } = useTopicEnrolmentQuery({
    variables: {
      id: Number(userTopicId),
      filter: filterAsQueryVariables
    }
  })

  if (loading) {
    return <Loader />
  }

  const enrolment = data?.analytics.workbooks.topicEnrolment

  if (!enrolment) {
    throw new Error('Enrolment not found')
  }

  const pointsTotal = enrolment.auto_correct
  const pointsAvailable =
    enrolment.available_points === 0
      ? enrolment.pass_mark
      : enrolment.available_points
  const startedAt = enrolment.start_date
    ? formatDateAndTimeShort(enrolment.start_date)
    : t`N/A`
  const updatedAt = enrolment.updated_at
    ? formatDateAndTimeShort(enrolment.updated_at)
    : t`N/A`
  const completedAt = enrolment.completion_date
    ? formatDateAndTimeShort(enrolment.completion_date)
    : t`N/A`

  const shareBatchAt = enrolment.share_batch_ts
    ? formatDateAndTimeShort(enrolment.share_batch_ts)
    : t`N/A`

  const renderQuizResults = (enrolment: EnrolmentDetailAnalyticsFragment) => {
    if (!enrolment || !enrolment.available_points) {
      // If this enrolment does not contain quiz, return null
      return null
    }
    let isInProgress = false
    const isScorm =
      enrolment.topicVersion?.topic_type === TopicVersionType.scorm
    const isVideoAssessment = videoAssessmentTopicVersionTypesForFE.includes(
      enrolment.topicVersion?.topic_type as TopicVersionType
    )

    if (
      (!isScorm &&
        !isVideoAssessment &&
        typeof enrolment.num_responses === 'number' &&
        enrolment.available_points > enrolment.num_responses) ||
      (isScorm && isVideoAssessment && !enrolment.completion_date)
    ) {
      isInProgress = true
    }

    let hasPassed = false
    if (typeof enrolment.auto_correct === 'number') {
      hasPassed = enrolment.auto_correct >= (enrolment.pass_mark || 0)
    }

    const classes = classNames({
      Workbook__points__total: true,
      'Workbook__points__total--in-progress': isInProgress
    })

    return (
      <>
        <div className={classes}>
          <strong>{getScoreString(pointsTotal, pointsAvailable)}</strong>
        </div>
        {isInProgress ? (
          <Tag color="blue">
            <Trans>Incomplete</Trans>
          </Tag>
        ) : enrolment.pass_mark ? (
          <>{hasPassed ? <StatusProgressPassed /> : <StatusProgressFailed />}</>
        ) : null}

        <Box color={brandColors.grey[500]} mt="12px" fontSize="14px">
          <Trans>
            Attempt {enrolment.attempt} of{' '}
            {enrolment.topicVersion?.max_attempts === 0
              ? '∞'
              : enrolment.topicVersion?.max_attempts}
          </Trans>
        </Box>
      </>
    )
  }

  return (
    <div className="Workbook">
      <AnalyticsHeader
        goBack={history.goBack}
        showFilterPanel={false}
        className="AnalyticsHeader--detail"
      >
        <>
          <div className="AnalyticsHeader--detail__content">
            <LinkableUserAvatar user={enrolment.student} />
            <div className="AnalyticsHeader__title__text">
              <small>
                {enrolment.student?.display_name}{' '}
                <span className="AnalyticsHeader__title__text__subtitle">
                  {enrolment.completion_date ? t`completed` : t`working on`}
                </span>{' '}
              </small>
              <div>
                {enrolment.topic.currentTitle}
                {enrolment.topic?.id && (
                  <Link
                    isExternal
                    to={linkBuilder.topicUrl(enrolment.topic?.id)}
                    ml="8px"
                  >
                    <ButtonIcon
                      color={brandColors.grey['700']}
                      aria-label={t`Go to resource`}
                      hint={t`Go to resource`}
                      icon={<IconNewTab w="19px" h="18px" />}
                      circular
                      hintProps={{
                        placement: 'top'
                      }}
                      _hover={{
                        bgColor: 'white'
                      }}
                    />
                  </Link>
                )}
                {enrolment.deleted_at && <StatusArchivedGrey />}
              </div>
            </div>
          </div>
          <div className="AnalyticsHeader--detail__status">
            <EnrolmentProgressDetail enrolment={enrolment} />
          </div>
        </>
      </AnalyticsHeader>
      <div className="AnalyticsRow">
        <BorderedBox className="AnalyticsRow__box">
          <div className="Workbook__detail__wrapper">
            <div className="Workbook__detail">
              <h2>
                <Trans>Summary</Trans>
              </h2>
              <div className="Workbook__detail__info">
                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Shared</Trans>
                  </span>
                  <span>{shareBatchAt}</span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Shared by</Trans>
                  </span>
                  {enrolment.sharedBy ? (
                    <>
                      <UserAvatar user={enrolment.sharedBy} />
                      {enrolment.sharedBy.display_name}
                    </>
                  ) : (
                    <Trans>Self shared</Trans>
                  )}
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Started</Trans>
                  </span>
                  <span>{startedAt}</span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Completed</Trans>
                  </span>
                  <span>{completedAt}</span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Last active</Trans>
                  </span>
                  <span>{updatedAt}</span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Version</Trans>
                  </span>
                  <span>{enrolment.topicVersion?.version}</span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Rating</Trans>
                  </span>
                  <span>
                    {enrolment.rating && (
                      <SmallMarbleRating rating={enrolment.rating} />
                    )}
                  </span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Version date</Trans>
                  </span>
                  <span>
                    {formatDateAndTimeShort(enrolment.topicVersion?.created_at)}
                  </span>
                </div>

                <div className="Workbook__detail__item">
                  <span className="Workbook__label">
                    <Trans>Feedback</Trans>
                  </span>
                  <span>{enrolment.feedback}</span>
                </div>
              </div>
            </div>

            <div className="Workbook__points">
              <span className="Workbook__label">
                <Trans>Score</Trans>
              </span>
              {renderQuizResults(enrolment)}
            </div>
          </div>
        </BorderedBox>
      </div>
      <QuizResults quizResults={enrolment.quizzesResults} />
    </div>
  )
}

export default WorkbooksDetails
